import type { ClassId } from '../core/constants.ts';

/**
 * What the pause screen tells you.
 *
 * Kept as data rather than as markup because the right-hand panel is different
 * for every class, and a card assembled from a table is one that cannot drift
 * out of step with itself — the old prompt was a paragraph listing R for raise
 * and F for call to every player in the game, most of whom are not Clerics and
 * cannot do either.
 *
 * Written as bullets rather than prose throughout. Somebody reading this has
 * their mouse unlocked in the middle of a match and wants one fact, not a
 * paragraph containing it.
 */

export interface Key {
  /** The key itself, which is the bullet. */
  press: string;
  does: string;
}

/**
 * True for everybody, whatever they are playing.
 *
 * Deliberately includes the things a *dead* mage does. Every class flies as a
 * ghost and every class can call on its own shrine, and a player learning that
 * only after dying has learned it at the worst possible moment.
 */
export const GENERAL_KEYS: readonly Key[] = [
  { press: 'W A S D', does: 'Move' },
  { press: 'Shift', does: 'Run' },
  { press: 'Space', does: 'Jump' },
  { press: 'C · Ctrl', does: 'Crouch' },
  { press: '1 – 6', does: 'Ready a spell' },
  { press: 'Click', does: 'Cast the readied spell' },
  { press: 'Tab', does: 'Buffs: Swap between elements' },
  { press: 'Enter', does: 'Team Chat' },
  { press: 'Shift + Enter', does: 'All Chat' },
  { press: 'Escape', does: 'Release the mouse' },
];

/** And what a mage does once they are dead, which is also true for everybody. */
export const GHOST_KEYS: readonly Key[] = [
  { press: 'Space', does: 'Rise' },
  { press: 'C · Ctrl', does: 'Down' },
  { press: 'F', does: 'Enter Valhalla (lose exp)' },
];

export interface Primer {
  /**
   * Where this class's power comes from, and what it is for.
   *
   * `{ley}` is replaced with the word in its own colour. Ley is a proper noun in
   * this game — it has a bar, a colour and four completely different rules — and
   * the one thing a new player most needs to know is which of the four is
   * theirs.
   */
  facts: readonly string[];
  /** Keys this class has and the others do not. */
  keys: readonly Key[];
}

export const PRIMERS: Record<ClassId, Primer> = {
  cleric: {
    facts: [
      'Your {ley} and power rise from being near your own shrine.',
      'Defend your shrine, and raise allies when they fall.',
      'You are the only class that can heal, and raise the dead.',
    ],
    keys: [
      { press: 'F', does: 'Call the nearest fallen ally to your feet' },
      { press: 'R', does: 'Raise a ghost lying at your feet' },
    ],
  },

  magician: {
    facts: [
      'Your {ley} comes from magic pools. Bias them to your side.',
      'Entering a pool stops your regeneration during biasing.',
      'You do the most damage in the game, but with lower health.',
    ],
    keys: [],
  },

  mentalist: {
    facts: [
      'Your {ley} is your own; never affected by pools or shrines.',
      'You can fly, which no other living mage can do.',
      'Scout, harass, and go where the map says nobody can.',
    ],
    keys: [
      { press: 'Space', does: 'Rise, while flying' },
      { press: 'C · Ctrl', does: 'Sink, while flying' },
    ],
  },

  arcanist: {
    facts: [
      'Your {ley} charges to full in any pool, whoever holds it.',
      'Then it falls away quickly, so you must keep returning to water.',
      'You cannot claim pools. Roam, take fights and lay your Signs.',
    ],
    keys: [],
  },
};
